"use client";

import * as React from "react";
import dynamic from "next/dynamic";
import {
  FileHtmlIcon,
  ArrowsOutSimpleIcon,
  InfoIcon,
} from "@phosphor-icons/react";
import { MediaSource } from "@/shared/types";
import { caseIdFromTags } from "@/shared/functions";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { markdownRehypeSanitizePlugins } from "@/lib/markdown-rehype-plugins";
import { markdownComponents } from "@/components/panes/presentationMarkdown";
import { Button } from "@tranquility-ai/theme-components/button";

/** Same component and `s3Url` resolution as `mediaDisplay.tsx` (markdown branch). */
const MarkdownViewer = dynamic(
  () => import("@/components/markdown-viewer/MarkdownViewer"),
  { ssr: false },
);

/** Mirrors `MarkdownViewer s3Url={displayItem?.srcToUse || displayItem?.mediaUrl || displayItem?.mediaSrc || ''}` in mediaDisplay. */
function markdownViewerS3Url(mediaItem: MediaSource | undefined, src: string): string {
  const fromItem =
    (mediaItem?.srcToUse && mediaItem.srcToUse.trim()) ||
    mediaItem?.mediaUrl ||
    mediaItem?.mediaSrc ||
    "";
  return fromItem || src.trim() || "";
}

export type MetaPreviewViewerProps = {
  src: string;
  mediaItem: MediaSource | undefined;
  /** When set, included on /meta-viewer links; falls back to ``caseIdFromTags(mediaItem.fileInfo.tags)``. */
  caseId?: string | null;
  isFullscreen?: boolean;
  isUnifiedSources?: boolean;
  isAccordion?: boolean;
  isTable?: boolean;
  /** Same outer box as standalone markdown in `mediaDisplay` (`width − 70px`, `height − toolbar`). */
  previewWidthPx?: number;
  previewHeightPx?: number;
  /** When false, the SMV button is not rendered (e.g. source card renders {@link MetaSocialViewerButton} above the citation). */
  hideSocialViewerButton?: boolean;
};

/** Meta thread previews use .md paths; mediaType may be `text` or `markdown` after preview content-type mapping (#775). */
const isMarkdownFile = (mediaItem: MediaSource | undefined, src: string) => {
  if (!mediaItem) return false;
  const textLike =
    mediaItem.mediaType === "text" || mediaItem.mediaType === "markdown";
  const url =
    mediaItem.previewFilePath ||
    src ||
    mediaItem.mediaUrl ||
    mediaItem.mediaSrc ||
    mediaItem.originalUrl ||
    "";
  const pathOnly = url.split(/[?#]/)[0].toLowerCase();
  const looksMarkdown =
    pathOnly.endsWith(".md") || pathOnly.endsWith(".markdown");
  return textLike && looksMarkdown;
};

/**
 * Derive s3Key for meta-viewer URL (same logic as Evidence Locker in question-bar).
 */
function getS3KeyForMetaViewer(
  src: string,
  mediaItem: MediaSource | undefined,
): string {
  const url =
    mediaItem?.originalUrl ??
    mediaItem?.mediaUrl ??
    mediaItem?.mediaSrc ??
    mediaItem?.sourceUrl ??
    src;
  if (!url) return "";
  return url.startsWith("s3://") ? url.split("/").slice(3).join("/") : url;
}

/** Build Social Media Viewer URL with optional caseId, threadID, or null if no s3Key. */
function getMetaViewerUrl(
  src: string,
  mediaItem: MediaSource | undefined,
  caseId: string | null | undefined,
  threadID: string | null,
  taiHash: string | null,
): string | null {
  const s3Key = getS3KeyForMetaViewer(src, mediaItem);
  if (!s3Key) return null;
  const caseParam =
    caseId && caseId.trim() !== ""
      ? `&caseId=${encodeURIComponent(caseId)}`
      : "";
  return `/meta-viewer?s3Key=${encodeURIComponent(s3Key)}${caseParam}${threadID ? `&threadID=${encodeURIComponent(threadID)}` : ""}${taiHash ? `#messageHash-${encodeURIComponent(taiHash)}` : ""}`;
}

/** Renders a link to Social Media Viewer when url is set, otherwise plain text. */
function SocialMediaViewerLink({
  url,
  children,
}: {
  url: string | null;
  children: React.ReactNode;
}) {
  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="min-w-0 break-words underline text-white cursor-pointer hover:text-white focus:outline-none focus:underline"
      >
        {children}
      </a>
    );
  }
  return <>{children}</>;
}

type MetaMessageEntry = {
  author: string;
  sent: string;
  body: string;
  taiHash: string;
};

const LABELS = ["Sent:", "Body:", "TAI Hash:", "Author:"];

/**
 * Parse meta export format.
 *
 * Note that this is just doing a best effort. The excerpt doesn't always start / end at the same token
 * and so sometimes information is incomplete. That said, I think this is better than just rendering the
 * markdown as-is which is not any better than the excerpt itself rendered as text (which we're already doing).
 * Perfection is the enemy of good.
 *
 * Records are separated by ---.
 * After the first, each block can start with **TAI Hash:**.
 * Author is either **Author:** name or ** name ** before **Sent:**.
 * */
function parseMetaMessageFormat(content: string): MetaMessageEntry[] {
  const messageEntries: MetaMessageEntry[] = [];
  const blocks = content
    .split(/\s*---\s*/)
    .map((b) => b.trim())
    .filter(Boolean);
  for (let i = 0; i < blocks.length; i++) {
    let block = blocks[i];

    // Block may start with **TAI Hash:** <value> (belongs to previous record, or junk). Strip it so we never treat "TAI Hash" as author.
    const leadingTaiHashMatch = block.match(
      /^\*\*TAI Hash:\*\*\s*([a-f0-9]+)\s*/,
    );
    if (leadingTaiHashMatch) {
      if (messageEntries.length > 0)
        messageEntries[messageEntries.length - 1].taiHash =
          leadingTaiHashMatch[1];
      block = block.slice(leadingTaiHashMatch[0].length).trim();
    } else {
      const stripLeadingTaiHash = block.match(/^\*\*TAI Hash:\*\*\s*[^\*]*/);
      if (stripLeadingTaiHash) {
        block = block.slice(stripLeadingTaiHash[0].length).trim();
      }
    }

    // Author: explicit **Author:** value, or first ** X ** before **Sent:** (X not a label)
    let author = "";
    const explicitAuthorMatch = block.match(
      /\*\*Author:\*\*\s*([^*]+?)(?=\s*\*\*Sent:\*\*)/,
    );
    if (explicitAuthorMatch) {
      author = explicitAuthorMatch[1].trim();
    } else {
      const legacyAuthorMatch = block.match(
        /^\*\*\s*([^*:]+?)\s*\*\*\s*\*\*Sent:\*\*/,
      );
      if (
        legacyAuthorMatch &&
        !LABELS.some((l) => legacyAuthorMatch[1].trim().startsWith(l))
      ) {
        author = legacyAuthorMatch[1].trim();
      }
    }

    const sentMatch = block.match(
      /\*\*Sent:\*\*\s*([^*]+?)(?=\s*\*\*Body:\*\*)/,
    );
    const sent = sentMatch ? sentMatch[1].trim() : "";
    const bodyMatch = block.match(
      /\*\*Body:\*\*\s*([\s\S]*?)(?=\s*\*\*TAI Hash:\*\*|$)/,
    );
    const body = bodyMatch ? bodyMatch[1].trim() : "";
    const hashAtEndMatch = block.match(/\*\*TAI Hash:\*\*\s*([a-f0-9]+)\s*$/);
    const recordHash = hashAtEndMatch ? hashAtEndMatch[1] : "";
    if (author || sent || body || recordHash) {
      messageEntries.push({ author, sent, body, taiHash: recordHash });
    }
  }
  return messageEntries;
}

/**
 * Look through content for a thread ID (e.g. **Thread ID:** value) and return it if present.
 */
function parseThreadID(object: MediaSource | undefined): string | null {
  if (!object) return null;

  // NOTE -- Jake Mason | (2026-03-06):
  // Okay this is hacky as hell to get the thread ID _but_ the BE team says that if we have a previewFilePath
  // on a media object, and it ends in "markdown/threads/{thread_id}.md" then we know that thread ID is valid.
  // So, first we look for it in the filepath...
  const path = object.previewFilePath;
  if (path?.includes("markdown/threads")) {
    const filename = path.split("/").pop() ?? "";
    if (filename.endsWith(".md")) {
      return filename.slice(0, -3);
    }
  }

  // ...and if we don't find it, we fall back to parsing the content to see if there's a thread ID in it.
  const content = object?.content?.trim() ?? "";
  if (!content?.trim()) return null;
  const match = content.match(
    /\*\*Thread\s*ID:\*\*\s*([^\n*]+?)(?=\s*---|\s*\*\*|\s*$)/i,
  );
  return match ? match[1].trim() : null;
}

/**
 * Find the first valid TAI Hash in content (**TAI Hash:** followed by hex chars) and return it.
 */
function parseFirstTaiHash(content: string | undefined): string | null {
  if (!content?.trim()) return null;
  const match = content.match(/\*\*TAI Hash:\*\*\s*([a-f0-9]+)/);
  return match ? match[1] : null;
}

function looksLikeMetaMessageFormat(content: string): boolean {
  return (
    /\*\*Sent:\*\*/.test(content) &&
    /\*\*Body:\*\*/.test(content) &&
    /\*\*TAI Hash:\*\*/.test(content)
  );
}

/** Resolves `/meta-viewer?...` for a meta bundle media object (shared with {@link MetaSocialViewerButton}). */
export function getMetaViewerOpenUrl(
  src: string,
  mediaItem: MediaSource | undefined,
  caseIdProp?: string | null,
): string | null {
  if (!mediaItem) return null;
  const content = mediaItem.content?.trim() ?? "";
  const taiHash = parseFirstTaiHash(content);
  const threadID = parseThreadID(mediaItem);
  const taggedCaseId = caseIdFromTags(mediaItem.fileInfo?.tags ?? undefined);
  const propOrSourceCase =
    (caseIdProp != null && String(caseIdProp).trim() !== ""
      ? String(caseIdProp).trim()
      : undefined) ??
    (taggedCaseId != null && taggedCaseId.trim() !== "" ? taggedCaseId.trim() : undefined) ??
    null;
  return getMetaViewerUrl(src, mediaItem, propOrSourceCase, threadID, taiHash);
}

/** Toolbar row used in {@link MetaFileDisplay} or above the citation block in source cards. */
export function MetaSocialViewerButton({
  src,
  mediaItem,
  caseId,
  className = "",
}: {
  src: string;
  mediaItem: MediaSource | undefined;
  caseId?: string | null;
  className?: string;
}) {
  const metaViewerUrl = getMetaViewerOpenUrl(src, mediaItem, caseId);
  if (!metaViewerUrl) return null;
  return (
    <div
      className={`w-full min-w-0 max-w-full flex-shrink-0 border-b border-white/20 p-2 ${className}`.trim()}
    >
      <Button
        size="sm"
        style="solid"
        color="accent"
        background="inverse"
        trailingIcon={<ArrowsOutSimpleIcon />}
        onClick={() => window.open(metaViewerUrl, "_blank")}
      >
        View in Social Media Viewer
      </Button>
    </div>
  );
}

export function MetaFileDisplay({
  src,
  mediaItem,
  caseId: caseIdProp,
  isFullscreen = false,
  isUnifiedSources = false,
  isAccordion = false,
  isTable = false,
  previewWidthPx,
  previewHeightPx,
  hideSocialViewerButton = false,
}: MetaPreviewViewerProps) {
  if ((isUnifiedSources || isAccordion || isTable) && !isFullscreen) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-600 rounded-md">
        <FileHtmlIcon size={25} />
      </div>
    );
  }

  const isMarkdown = isMarkdownFile(mediaItem, src);
  const content = mediaItem?.content?.trim() ?? "";
  const isMetaFormat = looksLikeMetaMessageFormat(content);
  const threads = isMetaFormat ? parseMetaMessageFormat(content) : [];
  const threadID = parseThreadID(mediaItem);
  const metaViewerUrl = getMetaViewerOpenUrl(src, mediaItem, caseIdProp);

  const s3ForMarkdownViewer = markdownViewerS3Url(mediaItem, src);

  const hasPreviewBox =
    !isFullscreen &&
    previewWidthPx != null &&
    previewHeightPx != null &&
    previewWidthPx > 0 &&
    previewHeightPx > 0;

  const previewBoxStyle: React.CSSProperties | undefined =
    hasPreviewBox
      ? {
          width: previewWidthPx,
          maxWidth: previewWidthPx,
          height: previewHeightPx,
          maxHeight: previewHeightPx,
        }
      : undefined;

  /** Outer shell: fixed size only when there is no thread UI (same as standalone markdown card). */
  const rootStyle: React.CSSProperties | undefined =
    isMetaFormat && threads.length > 0
      ? isFullscreen
        ? { minHeight: 0, flex: 1 }
        : undefined
      : isFullscreen
        ? { width: "100%", height: "100%", minHeight: 0, flex: 1 }
        : hasPreviewBox
          ? previewBoxStyle
          : undefined;

  return (
    <div
      className={`box-border flex w-full min-w-0 max-w-full flex-col ${
        isMetaFormat && threads.length > 0
          ? isFullscreen
            ? "h-full max-h-full min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
            : "items-stretch gap-2 overflow-y-auto overflow-x-hidden"
          : `overflow-hidden ${
              isFullscreen
                ? "h-full max-h-full min-h-0 flex-1"
                : hasPreviewBox
                  ? ""
                  : "h-[440px] max-h-full"
            }`
      }`}
      style={rootStyle}
      data-meta-file
    >
      {metaViewerUrl && !hideSocialViewerButton ? (
        <MetaSocialViewerButton src={src} mediaItem={mediaItem} caseId={caseIdProp} />
      ) : null}
      {isMarkdown && content ? (
        isMetaFormat && threads.length > 0 ? (
          <div className="box-border flex w-full min-w-0 max-w-full flex-col gap-2 overflow-x-hidden px-3">
            {/* 1. Disclaimer + thread ID — same content width as threads + markdown (single column). */}
            <div
              className="box-border mb-1.5 flex w-full min-w-0 max-w-full shrink-0 flex-row items-start gap-3 rounded-lg border border-blue-400/50 bg-blue-500/10 p-3 text-amber-200"
              role="status"
              aria-live="polite"
            >
              <InfoIcon
                size={20}
                weight="fill"
                className="mt-0.5 flex-shrink-0 text-blue-400"
              />
              <p className="m-0 min-w-0 flex-1 text-sm leading-snug text-white/90 [overflow-wrap:anywhere]">
                <span className="block min-w-0">
                  This is a preview of thread and may not contain all author
                  and time information.{" "}
                  <SocialMediaViewerLink url={metaViewerUrl}>
                    View the full file in Social Media Viewer
                  </SocialMediaViewerLink>{" "}
                  to see the entire conversation with complete information.
                </span>
                {threadID ? (
                  <>
                    <span className="mt-2 block min-w-0">
                      We found a thread ID inside this entry. You can{" "}
                      <SocialMediaViewerLink url={metaViewerUrl}>
                        view the full conversation in Social Media Viewer
                      </SocialMediaViewerLink>{" "}
                      to see the entire thread.
                    </span>
                    <span className="mt-2 block min-w-0 break-all">
                      Thread ID: <b className="font-semibold">{threadID}</b>
                    </span>
                  </>
                ) : null}
              </p>
            </div>
            {/* 2. Thread messages — same width as disclaimer + markdown. */}
            <div className="w-full min-w-0 max-w-full shrink-0 overflow-x-hidden pb-2">
              <ul className="m-0 flex min-w-0 list-none flex-col gap-3 p-0">
                {threads.map((r, i) => (
                  <li
                    key={`${r.taiHash}-${i}`}
                    className="min-w-0 max-w-full border border-white/20 rounded-lg bg-white/5 p-3 text-sm font-light text-white"
                  >
                    <div className="mb-1 flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="min-w-0 break-words font-semibold text-blue-300">
                        {r.author ? r.author : "Unknown"}
                      </span>
                      <span className="min-w-0 break-words text-xs text-white/60">{r.sent}</span>
                    </div>
                    {r.body ? (
                      <p className="m-0 max-w-full break-words leading-snug text-white/90">
                        {r.body}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
            {/* 3. Markdown preview — same content width as disclaimer + threads. */}
            <div
              className="box-border mt-2 flex w-full min-w-0 max-w-full shrink-0 flex-col overflow-hidden rounded-sm border-t border-white/20 bg-white pt-2 text-black"
              style={
                hasPreviewBox
                  ? {
                      width: "100%",
                      maxWidth: "100%",
                      height: previewHeightPx,
                      maxHeight: previewHeightPx,
                    }
                  : {
                      width: "100%",
                      height: 440,
                      maxHeight: 440,
                    }
              }
            >
              {s3ForMarkdownViewer ? (
                <MarkdownViewer
                  key={s3ForMarkdownViewer}
                  s3Url={s3ForMarkdownViewer}
                  className="h-full min-h-0"
                />
              ) : (
                <div className="custom-scrollbar2 h-full overflow-y-auto overflow-x-hidden p-4">
                  <ReactMarkdown
                    components={markdownComponents}
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={markdownRehypeSanitizePlugins}
                  >
                    {content}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="box-border flex flex-1 min-h-0 flex-col overflow-hidden rounded-sm bg-gray-600 text-black">
            {s3ForMarkdownViewer ? (
              <MarkdownViewer
                key={s3ForMarkdownViewer}
                s3Url={s3ForMarkdownViewer}
                className="h-full min-h-0"
              />
            ) : (
              <div className="custom-scrollbar2 flex-1 overflow-y-auto overflow-x-hidden p-4">
                <ReactMarkdown
                  components={markdownComponents}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={markdownRehypeSanitizePlugins}
                >
                  {content}
                </ReactMarkdown>
              </div>
            )}
          </div>
        )
      ) : null}
    </div>
  );
}

export default MetaFileDisplay;
